import type { RouteDetail } from '@motogo/shared';
import { useCallback, useEffect, useState } from 'react';

import { fetchRouteDetail } from '@/lib/savedRoutes';
import { getLocalRoute, putLocalRoute } from '@/lib/routeStore';

export type SavedRouteDetailState =
  | { status: 'loading' }
  | { status: 'ready'; route: RouteDetail; fromMirror: boolean }
  | { status: 'notfound' }
  | { status: 'error'; error: string };

/**
 * Loads one saved route, server-first with the IndexedDB mirror as offline fallback.
 * A successful server fetch refreshes the mirror, so the last-viewed copy of every
 * route is available in a dead zone.
 */
export function useSavedRouteDetail(id: string | undefined): {
  state: SavedRouteDetailState;
  refresh: () => void;
} {
  const [state, setState] = useState<SavedRouteDetailState>({ status: 'loading' });

  const load = useCallback(async () => {
    if (!id) {
      setState({ status: 'notfound' });
      return;
    }
    setState({ status: 'loading' });

    try {
      const route = await fetchRouteDetail(id);
      if (route) {
        await putLocalRoute(route, true).catch(() => undefined); // mirror refresh, best-effort
        setState({ status: 'ready', route, fromMirror: false });
        return;
      }
      // 404/401 — a not-yet-synced local save is still viewable.
      const local = await getLocalRoute(id);
      setState(local ? { status: 'ready', route: local, fromMirror: true } : { status: 'notfound' });
    } catch (e) {
      const local = await getLocalRoute(id);
      if (local) {
        setState({ status: 'ready', route: local, fromMirror: true });
      } else {
        setState({ status: 'error', error: e instanceof Error ? e.message : 'Failed to load the route' });
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, refresh: load };
}
